import React, { useState, useRef, useEffect } from "react";
import { Pagination, notification } from "antd";
import { useHistory, useLocation } from "react-router-dom";

import { scopeApiInstance } from "../../../dataProvider/axiosHelper";
import { Strings } from "../../../dataProvider/localize";
import { getSerializedFilters } from "../../../utils/common";
import TableView from "./table/TableView";
import FiltersView from "./filters/FiltersView";

export default function FilterTableViewV2({ configData, refreshRef = { current: 1 }, initialFilters = {}, clearInitial, alterDataTable, customTableData = [] }) {

  const controller = useRef();
  const location = useLocation();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filterValues, setFilterValues] = useState({});
  const [tableData, setTableData] = useState(customTableData);
  const [totalRows, setTotalRows] = useState(0);
  const [refreshKey, setRefreshKey] = useState(Date.now()); // Used to signal refresh

  const [initialFiltersLoaded, setInitialFiltersLoaded] = useState(false);

  const [activeModal, setActiveModal] = useState(null);
  const [modalData, setModalData] = useState(null);

  const fetchTableData = async (pageNo, filters = {}) => {
    setLoading(true);
    if (page !== pageNo) {
      setPage(pageNo)
    }
    let allFilters = {}
    if (((initialFilters && Object.keys(initialFilters).length > 0) || (configData && configData.initialFilters && Object.keys(configData.initialFilters).length > 0)) && !initialFiltersLoaded) {
      setFilterValues({ ...configData.initialFilters, ...initialFilters, });
      allFilters = { ...configData.initialFilters, ...initialFilters };
      setInitialFiltersLoaded(true);
      if (clearInitial) {
        clearInitial();
      }
    } else {
      setFilterValues({ ...filters });
      allFilters = { ...filters };
    }

    configData.filters.forEach(f => {
      if (f.submitModifier) {
        allFilters = f.submitModifier(allFilters)
      }
    })

    allFilters = await getSerializedFilters(allFilters);
    controller.current = new AbortController();
    let fetchParams = {};
    if (configData.fetchParams && configData.fetchParams.length > 0) {
      configData.fetchParams.forEach(param => {
        fetchParams[param.key] = param.value
      })
    }
    let fetchHeaders = {};
    if (configData.fetchHeaders && configData.fetchHeaders.length > 0) {
      configData.fetchHeaders.forEach(header => {
        fetchHeaders[header.key] = header.value
      })
    }
    return scopeApiInstance.get(
      configData.apiEndPoint,
      { params: { page: pageNo, ...fetchParams, ...allFilters }, signal: controller.current.signal, headers: fetchHeaders },
    ).then(res => {
      setLoading(false);
      if (configData.dataTable.dataGetter) {
        let [data, count] = configData.dataTable.dataGetter(res.data && res.data.data ? res.data.data : {})
        setTableData(data);
        setTotalRows(count)
      } else {
        setTableData(res.data.data && res.data.data.rows ? res.data.data.rows : []);
        setTotalRows(res.data.data && res.data.data.count ? res.data.data.count : 0);
      }
      setRefreshKey(Date.now());
    }).catch((err) => {
      if (err) {
        setLoading(false);
        notification.error({
          message: Strings.error_title,
          description: "There was an error while fetching Data.",
          className: "ant-error"
        })
      }
    })
  }

  const onFilter = (filters = filterValues, pageNo = page) => {
    if (configData && configData.apiEndPoint) {
      fetchTableData(pageNo, filters)
    }
  }

  useEffect(() => {
    if (configData && configData.apiEndPoint) {
      fetchTableData(1, location.searchParam && location.searchParam ? { [configData.searchableKey]: location.searchParam } : {});
    }
    return () => {
      if (controller.current) {
        controller.current.abort()
      }
    };
  }, [configData, location.searchParam]);

  const onModalTrigger = (key, data) => {
    setModalData(data);
    setActiveModal(key);
  }

  const onDefaultClose = (reloadData = true) => {
    setActiveModal(null);
    setModalData(null);
    if (reloadData) {
      onFilter(filterValues, page);
    }
  }

  return (
    <div className="container-fluid px-0">
      {configData.headingView ? <configData.headingView onModalTrigger={onModalTrigger} routerHistory={history} /> : null}
      <div className="row">
        <div className="col-12 px-1">
          <FiltersView
            tableLoading={loading}
            onFilter={(v) => onFilter(v, 1)}
            initialFilters={configData.initialFilters ? configData.initialFilters : {}}
            filterValues={filterValues}
            onDefaultClose={onDefaultClose}
            onModalTrigger={onModalTrigger}
            setFilterValues={setFilterValues}
            filters={configData && configData.filters ? configData.filters : []}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <TableView
            parentLoading={loading}
            refreshKey={refreshKey}
            onRefresh={() => onFilter(filterValues, page)}
            dataTable={alterDataTable ? alterDataTable : configData.dataTable}
            rowData={tableData} />
        </div>
      </div>
      <div className="row">
        <div className="col-12 d-flex justify-content-end pt-1">
          <Pagination
            disabled={!totalRows}
            className="cs-pagination-v2"
            total={totalRows}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} ${configData.dataTable && configData.dataTable.pagination && configData.dataTable.pagination.text ? configData.dataTable.pagination.text : "rows"}`}
            onChange={(pageNo) => { fetchTableData(pageNo, filterValues) }}
            pageSize={configData.dataTable && configData.dataTable.pagination && configData.dataTable.pagination.pageSize ? configData.dataTable.pagination.pageSize : 10}
            current={page}
            defaultCurrent={1}
          />
        </div>
      </div>
      {configData.dataTable && configData.dataTable.modals ? configData.dataTable.modals.map(modal => <modal.modal key={modal.triggerKey} onDefaultClose={onDefaultClose} visible={modal.triggerKey === activeModal} rowData={modalData} />) : null}
    </div>
  )
}